import React from 'react'

import { IFormFocusHandle, FormFocusHandleSetter } from './form-focus-handle'

/**
 * Save-site helper: returns a stable [setter, handleRef] tuple.
 *
 * ```
 * const [setFocusHandle, focusHandleRef] = useFormFocusHandle()
 * // …render tree…
 * <FormFocusHandleContext.Provider value={setFocusHandle}>{form}</FormFocusHandleContext.Provider>
 * // …on save failure…
 * focusHandleRef.current?.focusFirstError()
 * ```
 *
 * The setter identity is stable, so passing it as a context value doesn't
 * churn descendants. The ref lags behind mount by one microtask (the
 * provider publishes in useEffect), which is fine — save failures always
 * happen after user interaction, long after mount.
 */
export function useFormFocusHandle(): [FormFocusHandleSetter, React.MutableRefObject<IFormFocusHandle | null>] {
  const ref = React.useRef<IFormFocusHandle | null>(null)
  const setter = React.useCallback<FormFocusHandleSetter>((handle) => {
    ref.current = handle
  }, [])
  return [setter, ref]
}
